import { useEffect, useRef } from 'react'

import { bottomWhiteSpaceSmall } from '@/constants'
import { useInfiniteQuery, useMutation } from '@api/hooks'
import { ActionCenterTaskType } from '@domain'
import { Box, Drawer, Skeleton, Stack, Typography } from '@mui/material'
import PackConvertApi, { PackConvertKeys } from '@services/packConverts.api'
import {
	PackConvert as PackConvertType,
	UpdatePackConversionRequest,
} from '@services/packConverts.types'
import { useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { StringParam, useQueryParam } from 'use-query-params'

import PackConvertItem from './PackConvertItem'
import {
	removePackConvertFromCache,
	restorePackConvertCache,
	snapshotPackConvertCache,
	updatePackConvertActionCenterCache,
} from './packConverts.utils'

type Props = {
	onClose: () => void
}

export default function PackConvert({ onClose }: Props) {
	const { marketBasketId } = useParams()
	const [activeTask] = useQueryParam('active-task', StringParam)
	const queryClient = useQueryClient()
	const prevRemainingRef = useRef<number | null>(null)

	const { data, isLoading } = useInfiniteQuery({
		queryKey: PackConvertKeys.list(marketBasketId),
		queryFn: ({ pageParam = 1 }) => PackConvertApi.list(marketBasketId, pageParam),
		getNextPageParam: (lastPage) => {
			const { page, totalPages } = lastPage.pagination
			return page < totalPages ? page + 1 : undefined
		},
		enabled: activeTask === ActionCenterTaskType.PACK_CONVERSION && Boolean(marketBasketId),
		initialPageParam: 1,
	})

	const handleUpdatePackConvert = async ({
		purchaseMfgProdDmid,
		candidateMfgProdDmid,
	}: UpdatePackConversionRequest) => {
		await queryClient.cancelQueries({ queryKey: PackConvertKeys.list(marketBasketId) })

		const snapshot = snapshotPackConvertCache(queryClient, marketBasketId)
		removePackConvertFromCache(
			queryClient,
			marketBasketId,
			purchaseMfgProdDmid,
			candidateMfgProdDmid
		)

		return snapshot
	}

	const handleUpdateError = (
		_error: unknown,
		_request: UpdatePackConversionRequest,
		context: ReturnType<typeof snapshotPackConvertCache> | undefined
	) => {
		if (!context) return
		restorePackConvertCache(queryClient, marketBasketId, context)
	}

	const { mutate: update } = useMutation({
		mutationFn: (request: UpdatePackConversionRequest) =>
			PackConvertApi.update(Number(marketBasketId), request),
		onMutate: handleUpdatePackConvert,
		onError: handleUpdateError,
		onSettled: () =>
			queryClient.invalidateQueries({ queryKey: PackConvertKeys.list(marketBasketId) }),
	})

	const packConverts = data?.pages.flatMap((page) => page.data ?? []) ?? []
	const numToReview = data?.pages[0]?.pagination?.total ?? 0

	useEffect(() => {
		if (activeTask !== ActionCenterTaskType.PACK_CONVERSION) return

		const completedAllPackConverts =
			prevRemainingRef.current !== null && prevRemainingRef.current > 0 && numToReview === 0

		if (completedAllPackConverts) {
			updatePackConvertActionCenterCache(queryClient, marketBasketId)
			onClose()
		}

		prevRemainingRef.current = numToReview
	}, [activeTask, marketBasketId, numToReview, onClose, queryClient])

	return (
		<Drawer
			open={activeTask === ActionCenterTaskType.PACK_CONVERSION}
			variant="temporary"
			anchor="right"
			onClose={onClose}
			data-testid="pack-convert-drawer"
			sx={{ '& .MuiDrawer-paper': { width: '90vw' } }}
			keepMounted
		>
			<PackConvertUI
				packConverts={packConverts}
				numToReview={numToReview}
				isLoading={isLoading}
				onUpdate={(request) => update(request)}
			/>
		</Drawer>
	)
}

type UIProps = {
	packConverts: PackConvertType[]
	numToReview: number
	isLoading: boolean
	onUpdate: (request: UpdatePackConversionRequest) => void
}

export function PackConvertUI({ packConverts, numToReview, isLoading, onUpdate }: UIProps) {
	return (
		<Stack height="100%" overflow="hidden">
			<Typography variant="h6" px={3} py={2}>
				Pack Size Conversions ({numToReview})
			</Typography>

			{/* Scrollable list */}
			<Stack
				px={3}
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
					scrollbarGutter: 'stable',
					pb: bottomWhiteSpaceSmall,
				}}
			>
				{isLoading && (
					<>
						<Skeleton variant="rectangular" width="100%" height={124} sx={{ my: 1 }} />
						<Skeleton variant="rectangular" width="100%" height={124} sx={{ my: 1 }} />
						<Skeleton variant="rectangular" width="100%" height={124} sx={{ my: 1 }} />
						<Skeleton variant="rectangular" width="100%" height={124} sx={{ my: 1 }} />
					</>
				)}

				{!isLoading && packConverts.length === 0 && (
					<Box display="flex" alignItems="center" justifyContent="center" flex={1} sx={{ py: 4 }}>
						<Typography variant="body2" color="text.secondary">
							No pack size issues found
						</Typography>
					</Box>
				)}

				{!isLoading &&
					packConverts.map((pc, i) => (
						<PackConvertItem key={pc.id} packConvert={pc} onUpdate={onUpdate} autoFocus={i === 0} />
					))}
			</Stack>
		</Stack>
	)
}
